import {MapPosition} from '../../route/metamodel';
import {TaskEntry} from '../tasks-queue/model/task.entry';
import {DriverTaskPointEntry} from '../tasks-queue/driver-task-point.entry';
import {DateFormatUtils} from '../../../../../core/component/date-time-picker/date-format.utils';
import {AdditionalInfoEntry} from '../tasks-queue/model/additional-info.entry';
import {AdditionalInfoForm} from '../task-form/additional-info-form';
import DirectionsResult = google.maps.DirectionsResult;
import {TaskFormData} from '../task-form/task-form.data';
import {ContactlessType, ParkingType} from '../../pro-tasks-manager/form/pro-tasks-form-state.service';
import {FuelingPoiEntry} from '../../../../../core/component/fueling-poi/model/fueling-poi.entry';
import {TASKS_EXCLUDED_COUNTRIES, TASKS_TRAFFIC} from '../../../../../core/global/cookies.constants';
import {CookieService} from 'ngx-cookie';
import {
  applyPoiMarkerFill,
  normalizePoiMarkerColor,
  POI_MARKER_FILL
} from '../../../../../core/commons/color.utils';

export interface SimplifiedRoute {
  origin: MapPosition;
  destination: MapPosition;
  waypoints: MapPosition[];
}

export const CRT_ID = -10;
export const CIRCLE_K_ID = -250;
export const SHELL_ID = -150;
export const VIADA_ID = -300;
export const AS24_ID = -200;
export const CRT_CARDLESS_ID = -11;
export const CIRCLE_K_CONTACTLESS_ID = -251;
export const SHELL_CONTACTLESS_ID = -151;
export const BOSCH_PARKING_ID = -35;
export const TRAVIS_PARKING_ID = -40;

export const POI_ICON = `
    <svg width="72" height="72" xmlns="http://www.w3.org/2000/svg">
      <path fill-rule="evenodd" clip-rule="evenodd"
       d="m32.943 70.246-.27-.533c-3.546-9.272-7.982-15.615-12.001-21.36-.518-.741-1.029-1.471-1.53-2.197C14.77 39.826
        11 33.684 11 24.947 11 11.17 22.17 0 35.947 0c13.778 0 24.947 11.17 24.947 24.947 0 8.723-3.705 14.859-8.06
        21.2-.538.784-1.088 1.574-1.646 2.376-3.974 5.705-8.371 12.02-11.973 21.207l-.26.511c-.326.507-1.27 1.752-2.995
        1.759-1.736.007-2.689-1.242-3.017-1.754Zm1.432-4.123c-3.493-8.273-7.658-14.224-11.315-19.45-5.056-7.224-9.141-
        13.062-9.141-21.726 0-12.166 9.862-22.028 22.028-22.028 12.166 0 22.028 9.862 22.028 22.028 0 8.73-4.084 14.
        591-9.174 21.894-3.615 5.188-7.738 11.104-11.263 19.293a85.001 85.001 0 0 0-1.04
         2.531c-.355.553-.745.555-1.098.005a80.92 80.92 0 0 0-1.025-2.547Z"
        fill="#242D3A"/>
      <path d="M35.947 2.919c-12.166 0-22.028 9.862-22.028 22.028 0 15.557 13.173 22.003 21.48 43.723.353.55.744.548
       1.099-.005C45.009 46.95 57.975 40.5 57.975 24.947c0-12.166-9.862-22.028-22.028-22.028Z"
        fill="${POI_MARKER_FILL}"/>
      <g transform="translate(26, 11)"><circle cx="10" cy="10" r="10" fill="#FFFFFF"/></g>
    </svg>`;

export const POI_ICON_WITHOUT_CENTER = `
    <svg width="72" height="72" xmlns="http://www.w3.org/2000/svg">
      <path fill-rule="evenodd" clip-rule="evenodd"
       d="m32.943 70.246-.27-.533c-3.546-9.272-7.982-15.615-12.001-21.36-.518-.741-1.029-1.471-1.53-2.197C14.77 39.826
        11 33.684 11 24.947 11 11.17 22.17 0 35.947 0c13.778 0 24.947 11.17 24.947 24.947 0 8.723-3.705 14.859-8.06
        21.2-.538.784-1.088 1.574-1.646 2.376-3.974 5.705-8.371 12.02-11.973 21.207l-.26.511c-.326.507-1.27 1.752-2.995
        1.759-1.736.007-2.689-1.242-3.017-1.754Zm1.432-4.123c-3.493-8.273-7.658-14.224-11.315-19.45-5.056-7.224-9.141-
        13.062-9.141-21.726 0-12.166 9.862-22.028 22.028-22.028 12.166 0 22.028 9.862 22.028 22.028 0 8.73-4.084 14.
        591-9.174 21.894-3.615 5.188-7.738 11.104-11.263 19.293a85.001 85.001 0 0 0-1.04
         2.531c-.355.553-.745.555-1.098.005a80.92 80.92 0 0 0-1.025-2.547Z"
        fill="#242D3A"/>
      <path d="M35.947 2.919c-12.166 0-22.028 9.862-22.028 22.028 0 15.557 13.173 22.003 21.48 43.723.353.55.744.548
       1.099-.005C45.009 46.95 57.975 40.5 57.975 24.947c0-12.166-9.862-22.028-22.028-22.028Z"
        fill="${POI_MARKER_FILL}"/>
    </svg>`;

export class TaskUtils {


  public static simplifiedRoute(route: any): SimplifiedRoute {
    let startLocation = route.legs[0].start_location;
    return {
      origin: this.toMapPosition(startLocation ? startLocation : route.legs[0].startLocation),
      destination: this.routeDestination(route),
      waypoints: this.waypoints(route)
    };
  }

  public static routeDestination(route: any): MapPosition {
    let leg = route.legs[route.legs.length - 1];

    let endLoc = leg.end_location;
    let endLocation = this.toMapPosition(endLoc ? endLoc : leg.endLocation);
    return {
      lat: endLocation.lat,
      lng: endLocation.lng,
      address: leg.end_address
    };
  }

  public static routeOrigin(route: any): MapPosition {
    let leg = route.legs[0];
    let startLoc = leg.start_location;
    let startLocation = this.toMapPosition(startLoc ? startLoc : leg.startLocation);
    return {
      lat: startLocation.lat,
      lng: startLocation.lng,
      address: leg.start_address
    };
  }

  public static toMapPositions(waypoints: string): MapPosition[] {
    let wpts = [];
    if (waypoints) {
      let stringPoints = waypoints.split('|');
      for (let point of stringPoints) {
        let latLng = point.split(',');
        wpts.push({lat: latLng[0], lng: latLng[1]});
      }
    }
    return wpts;
  }

  public static latLngToMapPositions(waypoints: google.maps.LatLng[]): MapPosition[] {
    return waypoints.map(it => {
      return {
        lat: it.lat(),
        lng: it.lng(),
        address: null}
    });
  }

  public static toMapPosition(latLng: any): MapPosition {
    if (latLng instanceof google.maps.LatLng) {
      return {lat: latLng.lat(), lng: latLng.lng()};
    }
    return {lat: latLng.lat, lng: latLng.lng};
  }

  public static toOlMapPosition(lonLat: any): MapPosition {
    return {lat: lonLat[1], lng: lonLat[0]}
  }

  public static googleWaypoints(directions: DirectionsResult): any {
    let waypoints: google.maps.LatLng[] = [];
    let legs = directions.routes[0] ? directions.routes[0].legs : [];
    for (let leg of legs) {
      if (legs.indexOf(leg) !== 0) {
        waypoints.push(leg.start_location);
      }
      leg.via_waypoints.forEach(w => waypoints.push(w));
    }
    return waypoints;
  }

  public static waypoints(route: any): any {
    let waypoints: MapPosition[] = [];
    for (let leg of route.legs) {
      if (route.legs.indexOf(leg) !== 0) {
        waypoints.push(this.toMapPosition(leg.start_location ? leg.start_location : leg.startLocation));
      }
      if (leg.via_waypoints || leg.viaWaypoints) {
        leg.via_waypoints
          ? leg.via_waypoints.forEach(w => waypoints.push(this.toMapPosition(w)))
          : leg.viaWaypoints.forEach(w => waypoints.push(this.toMapPosition(w)));
      }
    }
    return waypoints;
  }

  public static getRoadsString(route: any): string {
    let roads: string[] = [];
    for (let leg of route.legs) {
      let roadNum = new RegExp(/[ANEDB]\d{1,4}/);
      leg.steps.forEach(step => {
        if (step.instructions) {
          let match = step.instructions.toString().match(roadNum);
          if (match != null && !roads.includes(match[0])) {
            roads.push(match[0]);
          }
        }
      });
    }
    return roads.join(', ');
  }

  public static distanceWithoutFerry(route: any): number {
    let distance = 0;
    for (let leg of route.legs) {
      leg.steps.forEach(step => {
        if (step.instructions.toString().match('ferry')) {
          distance += step.distance.value;
        }
      })
    }
    return distance;
  }

  public static typeTranslation(type: string, translations: any): string {
    if (type) {
      return translations.tasks.type[type]
    }
    return translations.tasks.type.null;

  }

  public static stringifyPoints(waypoints: MapPosition[]): string {
    return waypoints
      .map(it => TaskUtils.stringifyPoint(it))
      .join('|');
  }

  private static stringifyPoint(it: MapPosition) {
    return `${it.lat},${it.lng}`;
  }

  public static toTaskFormData(entry: TaskEntry, taskRoute, routeParams): TaskFormData {
    return {
      id: entry.id,
      type: entry.type,
      comments: entry.comments,
      routeInfo: entry.routeInfo,
      sendMessage: entry.sendMessage,
      originSearch: taskRoute.origin.address === null
          ? taskRoute.origin.latitude + ', ' + taskRoute.origin.longitude
          : taskRoute.origin.address,
      searchText: taskRoute.searchText,
      origin: this.toTaskMapPosition(taskRoute.origin),
      destination: this.toTaskMapPosition(taskRoute.destination),
      polyline: taskRoute.polyline,
      waypoints: taskRoute.waypoints,
      distance: taskRoute.distance,
      duration: taskRoute.duration,
      timeRemaining: entry.timeRemaining,
      distanceRemaining: entry.distanceRemaining,
      eta: entry.eta,
      distancePassed: entry.distancePassed,
      tolls: taskRoute.tolls,
      avoids: routeParams.avoids,
      strategy: routeParams.strategy,
      vehicleType: routeParams.vehicleType,
      weight: routeParams.weight,
      height: routeParams.height,
      width: routeParams.width,
      length: routeParams.length,
      infoSource: entry.infoSource,
      hazardous: routeParams.hazardous,
      hazardousType: routeParams.hazardousType,
      finishedRoute: entry.finishedRoute,
      additionalInfo: this.toAdditionalInfoForm(entry.additionalInfoEntries),
      fileName: entry.fileName,
      externalPoiId: entry.externalPoiId,
      contactlessType: entry.contactlessType,
      timeFrom: entry.timeFrom,
      timeTill: entry.timeTill,
      zoneId: entry.zoneId,
      dispatcherAddress: taskRoute.dispatcherAddress,
      startedAt: entry.startedAt,
      hideTaskUpdatedNotification: entry.hideTaskUpdatedNotification
    };
  }

  private static toTaskMapPosition(entry: DriverTaskPointEntry) {
    return {lat: entry.latitude, lng: entry.longitude, address: entry.address};
  }

  private static toAdditionalInfoForm(infoEntries: AdditionalInfoEntry[]): AdditionalInfoForm[] {
    let infoForm = [];
    for (let entry of infoEntries) {
      infoForm.push({
        optionId: entry.optionId,
        value: entry.value,
        type: entry.type,
      });
    }
    return infoForm;
  }

  private static getValue(entry: AdditionalInfoEntry) {
    switch (entry.type) {
      case 'LONG' || 'DOUBLE':
        return +entry.value;
      case 'DATE_TIME':
        return DateFormatUtils.localDateTimeStringToTimeString(entry.value);
    }
    return entry.value;
  }

  public static pointsAreDifferent(pointA: MapPosition, pointB: MapPosition): boolean {
    return pointA.lat.toFixed(3) !== pointB.lat.toFixed(3)
      || pointA.lng.toFixed(3) !== pointB.lng.toFixed(3);
  }

  public static isCRTStation(externalId: string): boolean {
    return externalId != null ? externalId.includes('drv') : false;
  }

  public static resolveContactlessType(poi: any): ContactlessType {
    if (poi?.externalId != null && poi.poiGroup?.id != null) {
      switch (poi.poiGroup.id) {
        case CIRCLE_K_CONTACTLESS_ID:
          return 'CIRCLE_K';
        case CRT_CARDLESS_ID:
          return 'CRT';
        case SHELL_CONTACTLESS_ID:
          return 'SHELL';
        default:
          return null;
      }
    }
    return null;
  }

  public static resolveContactless(poi: FuelingPoiEntry): ContactlessType {
    if (poi?.externalId != null && poi.contactless && poi.groupId != null) {
      switch (poi.groupId) {
        case CIRCLE_K_ID:
          return 'CIRCLE_K';
        case CRT_ID:
          return 'CRT';
        case SHELL_ID:
          return 'SHELL';
        case VIADA_ID:
          return 'VIADA';
        default:
          return null;
      }
    } else if (poi.groupId != null && AS24_ID === poi.groupId) {
      return 'AS24';
    }
    return null;
  }

  public static resolveParkingType(poi: any): ParkingType {
    if (poi?.externalId != null) {
      if (poi.poiGroup?.id === BOSCH_PARKING_ID) {
        return 'BOSCH';
      } else if (poi.poiGroup?.id === TRAVIS_PARKING_ID) {
        return 'TRAVIS'
      }
    }
    return null;
  }

  public static getCookiesTraffic(cookieService: CookieService): boolean {
    let traffic = cookieService.get(TASKS_TRAFFIC);
    return traffic != null
      ? traffic === 'true'
      : false;
  }

  public static getColoredPoiIcon(iconColor: string): string {
    let coloredSvg = applyPoiMarkerFill(POI_ICON, iconColor);
    return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(coloredSvg);
  }

  public static normalizePoiMarkerColor(color: string): string {
    return normalizePoiMarkerColor(color);
  }

  public static getPoiIconWithLogo(iconColor: string, logoUrl: string): Promise<string> {
    let normalizedColor = TaskUtils.normalizePoiMarkerColor(iconColor);
    let coloredPinSvg = applyPoiMarkerFill(POI_ICON_WITHOUT_CENTER, normalizedColor);
    let pinDataUrl = 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(coloredPinSvg);
    return new Promise(resolve => {
      let pinImage = new Image();
      pinImage.onload = () => {
        let logoImage = new Image();
        logoImage.crossOrigin = 'anonymous';
        logoImage.onload = () => {
          let canvas = document.createElement('canvas');
          canvas.width = 72;
          canvas.height = 72;
          let context = canvas.getContext('2d');
          context.drawImage(pinImage, 0, 0);
          TaskUtils.drawPoiMarkerLogo(context, logoImage);
          resolve(canvas.toDataURL());
        };
        logoImage.onerror = () => resolve(pinDataUrl);
        logoImage.src = logoUrl;
      };
      pinImage.onerror = () => resolve(TaskUtils.getColoredPoiIcon(normalizedColor));
      pinImage.src = pinDataUrl;
    });
  }

  private static drawPoiMarkerLogo(context: CanvasRenderingContext2D, logoImage: HTMLImageElement) {
    const logoMaxWidth = 44;
    const logoMaxHeight = 28;
    const logoTop = 8;
    const aspectRatio = logoImage.width / logoImage.height;
    let drawWidth = logoMaxWidth;
    let drawHeight = logoMaxWidth / aspectRatio;
    if (drawHeight > logoMaxHeight) {
      drawHeight = logoMaxHeight;
      drawWidth = logoMaxHeight * aspectRatio;
    }
    const drawX = (72 - drawWidth) / 2;
    const drawY = logoTop + (logoMaxHeight - drawHeight) / 2;
    context.drawImage(logoImage, drawX, drawY, drawWidth, drawHeight);
  }

  public static getCookiesExcludedCountries(cookieService: CookieService): string[] {
    let cookiesExcludedCountries = cookieService.get(TASKS_EXCLUDED_COUNTRIES);
    return cookiesExcludedCountries == null
      ? []
      : JSON.parse(cookiesExcludedCountries);
  }
}
